/**
 * Analytics event tracking via PostHog (loaded by Zaraz)
 *
 * PostHog is loaded via Cloudflare Zaraz for performance and privacy.
 * Events are sent to PostHog for product analytics.
 *
 * @see https://posthog.com/docs
 * @see docs/ARCHITECTURE_ANALYTICS_AND_SEO.md
 */

interface AnalyticsEvent {
  event: string;
  properties?: Record<string, unknown>;
}

/**
 * Track custom event via PostHog
 * Safely handles case where PostHog isn't loaded yet
 *
 * @param event - Event name (e.g., 'cta_clicked', 'resource_downloaded')
 * @param properties - Event properties (optional metadata)
 *
 * @example
 * trackEvent('cta_clicked', {
 *   location: 'hero',
 *   text: 'Get Started',
 *   path: window.location.pathname,
 * });
 */
export function trackEvent(event: string, properties?: Record<string, unknown>): void {
  if (typeof window === 'undefined') return;

  // Capture to PostHog directly (#739). Zaraz's AllTracks trigger binds no action,
  // so zaraz.track() events never reach PostHog — they vanish server-side.
  // Mirrors portalTrack() in apps/support-sbi/src/client/lib/analytics.ts.
  if (window.posthog?.capture) {
    flushPendingEvents();
    window.posthog.capture(event, properties);
    return;
  }

  // Zaraz loads PostHog after the first pageview, so an event fired at page load
  // (the thank-you conversions) comes before it. Hold it until PostHog is ready
  // instead of sending it to zaraz.track(), where it vanishes (#1879).
  enqueuePendingEvent(event, properties);
}

// ============================================================
// INTERNAL TRAFFIC (#10148)
// ============================================================

/**
 * #10148: the user-agent words of an AI or headless browser, counted as internal traffic.
 * `Claude/` is the Claude desktop app browser: PostHog 102930 shows it as `$browser = "Claude"`
 * (9 people in 30 days, every raw user agent holds `Claude/<version>`). `HeadlessChrome` is the
 * standard headless Chrome marker. It was not seen in the last 30 days. Add a name here only
 * after a PostHog read shows it.
 */
const INTERNAL_BROWSER_UA = /\bClaude\/|HeadlessChrome/;

/** #10148: true when the user agent names an AI or headless browser. */
export function isInternalBrowser(userAgent: string | null | undefined): boolean {
  return typeof userAgent === 'string' && INTERNAL_BROWSER_UA.test(userAgent);
}

let internalChecked = false;

/**
 * #10148: once PostHog is loaded, mark an AI or headless browser as internal. `register` adds
 * `internal_user = true` to every later event from this browser. `people.set` adds the same
 * property to the person. The PostHog test-account filter can then use `internal_user != true`.
 * It runs once per page load. A normal browser is left untouched.
 */
function markInternalTraffic(): void {
  if (internalChecked) return;
  const posthog = window.posthog;
  if (!posthog?.register) return;
  internalChecked = true;
  if (typeof navigator === 'undefined' || !isInternalBrowser(navigator.userAgent)) return;
  posthog.register({ internal_user: true });
  posthog.people?.set({ internal_user: true });
}

// ============================================================
// CTA LABELS (#9494)
// ============================================================

/**
 * #9494: a stable CTA label, '<page>-<placement>-<action>' in lowercase words joined by "-",
 * at most 80 characters. Every CTA click event carries it as `cta_label`, and the website
 * analytics tab counts clicks per label (api-sbs-router website-analytics-api.ts).
 */
export function ctaLabel(...parts: Array<string | null | undefined>): string {
  return parts
    .filter((p): p is string => typeof p === 'string' && p.trim().length > 0)
    .join('-')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80)
    .replace(/-+$/, '');
}

/** #9494: the label of a "Book a Free Consult" CTA at a placement. */
export const freeConsultCtaLabel = (location: string): string => ctaLabel(location, 'book-consult');
/** #9494: the label of an "Explore Programs" CTA at a placement. */
export const programsCtaLabel = (location: string): string => ctaLabel(location, 'explore-programs');
/** #9494: the label of a "Work With Us" or "Get Started" link to /get-started at a placement. */
export const getStartedCtaLabel = (location: string): string => ctaLabel(location, 'get-started');

/** #9494: the data-cta attribute, so a PostHog autocapture click carries the same label. */
export function ctaAttrs(label: string): { 'data-cta': string } {
  return { 'data-cta': ctaLabel(label) };
}

// ============================================================
// PENDING EVENTS — held until Zaraz has loaded PostHog (#1879)
// ============================================================

/** The most events held before PostHog loads. Later events past the cap are dropped. */
export const PENDING_EVENTS_MAX = 50;
/** How often the queue checks for PostHog. */
export const PENDING_FLUSH_INTERVAL_MS = 250;
/** How long the queue waits for PostHog before it hands the events to zaraz.track(). */
export const PENDING_FLUSH_TIMEOUT_MS = 15_000;

const pendingEvents: AnalyticsEvent[] = [];
let pendingTimer: ReturnType<typeof setInterval> | null = null;
let pendingWaitedMs = 0;

function stopPendingTimer(): void {
  if (pendingTimer !== null) clearInterval(pendingTimer);
  pendingTimer = null;
  pendingWaitedMs = 0;
}

function enqueuePendingEvent(event: string, properties?: Record<string, unknown>): void {
  if (pendingEvents.length >= PENDING_EVENTS_MAX) {
    if (import.meta.env.DEV) console.warn('[Analytics] Pending queue full, event dropped:', event);
    return;
  }
  pendingEvents.push({ event, properties });
  if (pendingTimer !== null) return;
  pendingTimer = setInterval(() => {
    pendingWaitedMs += PENDING_FLUSH_INTERVAL_MS;
    if (window.posthog?.capture) {
      flushPendingEvents();
    } else if (pendingWaitedMs >= PENDING_FLUSH_TIMEOUT_MS) {
      // PostHog never loaded (for example, blocked). Keep the old fallback, so no
      // event is lost that was not lost before.
      stopPendingTimer();
      for (const e of pendingEvents.splice(0)) window.zaraz?.track?.(e.event, e.properties);
    }
  }, PENDING_FLUSH_INTERVAL_MS);
}

/** Send every held event to PostHog, oldest first. A no-op until PostHog is loaded. */
function flushPendingEvents(): void {
  const capture = window.posthog?.capture;
  if (!capture) return;
  markInternalTraffic();
  stopPendingTimer();
  for (const e of pendingEvents.splice(0)) capture.call(window.posthog, e.event, e.properties);
}

/** The number of events held for PostHog. For tests and debugging. */
export function pendingEventCount(): number {
  return pendingEvents.length;
}

/** Clear the held events, the timer and the internal-traffic check. For tests only. */
export function resetPendingEventsForTests(): void {
  stopPendingTimer();
  pendingEvents.length = 0;
  internalChecked = false;
}

/**
 * Identify user (for logged-in users)
 * Only call this after user authentication
 *
 * @param userId - Unique user identifier
 * @param traits - User properties (email, name, etc.)
 *
 * @example
 * identifyUser('user_123', {
 *   email: 'user@example.com',
 *   name: 'John Doe',
 *   plan: 'pro',
 * });
 */
export function identifyUser(userId: string, traits?: Record<string, unknown>): void {
  if (typeof window === 'undefined') return;

  if (window.posthog?.identify) {
    window.posthog.identify(userId, traits);
  }
}

/**
 * Track page view (SPA navigation)
 * With Zaraz Automatic Pageview Tracking set to OFF, we must emit
 * pageviews manually for Zaraz trigger chains to execute.
 *
 * @param path - Current path
 * @param properties - Additional properties (title, referrer, etc.)
 *
 * @example
 * trackPageView('/about', {
 *   page_title: 'About Us',
 *   referrer: document.referrer,
 * });
 */
export function trackPageView(path: string, properties?: Record<string, unknown>): void {
  if (typeof window !== 'undefined' && window.zaraz) {
    // Preferred manual SPA pageview API per Zaraz docs.
    if (typeof window.zaraz.spaPageview === 'function') {
      window.zaraz.spaPageview();
    } else if (typeof window.zaraz.track === 'function') {
      // Compatibility fallback if spaPageview is unavailable.
      window.zaraz.track('Pageview');
    }
  }

  // Keep enriched app-level event for internal analysis in PostHog.
  trackEvent('spa_pageview', { path, ...properties });
}

/**
 * Reset analytics session (on logout)
 * Clears user identification and starts fresh session
 */
export function resetAnalytics(): void {
  if (typeof window === 'undefined') return;

  if (window.posthog?.reset) {
    window.posthog.reset();
  }
}

/**
 * Track error events for monitoring and debugging
 * Sends error information to analytics for tracking crash rates
 *
 * @param error - The error object
 * @param context - Additional context about where/how the error occurred
 *
 * @example
 * trackError(new Error('Failed to load data'), {
 *   source: 'DataFetcher',
 *   endpoint: '/api/users',
 * });
 */
export function trackError(error: Error, context?: Record<string, unknown>): void {
  trackEvent('client_error', {
    error_message: error.message,
    error_name: error.name,
    error_stack: error.stack?.slice(0, 500), // Limit stack trace length
    url: typeof window !== 'undefined' ? window.location.href : undefined,
    ...context,
  });
}

/**
 * Group users by organization/company
 * Useful for B2B analytics and cohort analysis
 *
 * @param groupType - Type of group (e.g., 'company', 'organization')
 * @param groupKey - Unique identifier for the group
 * @param groupProperties - Group properties (name, plan, etc.)
 *
 * @example
 * groupUser('company', 'acme-corp', {
 *   name: 'Acme Corp',
 *   plan: 'professional',
 *   employee_count: 50,
 * });
 */
export function groupUser(
  groupType: string,
  groupKey: string,
  groupProperties?: Record<string, unknown>
): void {
  if (typeof window === 'undefined') return;

  if (window.posthog?.group) {
    window.posthog.group(groupType, groupKey, groupProperties);
  }
}

/**
 * Alias anonymous user to identified user
 * Links pre-authentication activity to post-authentication identity
 *
 * @param alias - New user identifier
 * @param distinctId - Optional previous distinct ID (defaults to current)
 *
 * @example
 * // After user signs up, link their anonymous activity
 * aliasUser('user_12345');
 */
export function aliasUser(alias: string, distinctId?: string): void {
  if (typeof window === 'undefined') return;

  if (window.posthog?.alias) {
    window.posthog.alias(alias, distinctId);
  }
}

/**
 * Check if a feature flag is enabled
 *
 * @param flagKey - Feature flag key
 * @returns boolean indicating if flag is enabled, or undefined if not loaded
 *
 * @example
 * if (isFeatureEnabled('new-checkout-flow')) {
 *   // Show new checkout experience
 * }
 */
export function isFeatureEnabled(flagKey: string): boolean | undefined {
  if (typeof window === 'undefined') return undefined;

  if (window.posthog?.isFeatureEnabled) {
    return window.posthog.isFeatureEnabled(flagKey);
  }

  return undefined;
}

/**
 * Get feature flag value (supports multivariate flags)
 *
 * @param flagKey - Feature flag key
 * @returns Flag value (string, boolean, or undefined)
 *
 * @example
 * const variant = getFeatureFlag('checkout-variant');
 * if (variant === 'control') { ... }
 * if (variant === 'variant-a') { ... }
 */
export function getFeatureFlag(flagKey: string): string | boolean | undefined {
  if (typeof window === 'undefined') return undefined;

  if (window.posthog?.getFeatureFlag) {
    return window.posthog.getFeatureFlag(flagKey);
  }

  return undefined;
}

/**
 * Start session recording manually
 * Useful for conditional recording (e.g., after user consent)
 */
export function startSessionRecording(): void {
  if (typeof window === 'undefined') return;

  if (window.posthog?.startSessionRecording) {
    window.posthog.startSessionRecording();
  }
}

/**
 * Stop session recording
 */
export function stopSessionRecording(): void {
  if (typeof window === 'undefined') return;

  if (window.posthog?.stopSessionRecording) {
    window.posthog.stopSessionRecording();
  }
}

/**
 * Opt out of analytics tracking
 * Respects user privacy preferences
 */
export function optOutOfTracking(): void {
  if (typeof window === 'undefined') return;

  if (window.posthog?.opt_out_capturing) {
    window.posthog.opt_out_capturing();
  }
}

/**
 * Opt in to analytics tracking
 */
export function optInToTracking(): void {
  if (typeof window === 'undefined') return;

  if (window.posthog?.opt_in_capturing) {
    window.posthog.opt_in_capturing();
  }
}

/**
 * Check if analytics is loaded and ready
 * Useful for conditional tracking or debugging
 */
export function isAnalyticsReady(): boolean {
  if (typeof window === 'undefined') return false;
  return !!(window.zaraz?.track || window.posthog?.capture);
}

/**
 * Get current distinct ID (PostHog user identifier)
 * Useful for debugging or logging
 */
export function getDistinctId(): string | undefined {
  if (typeof window === 'undefined') return undefined;

  if (window.posthog?.get_distinct_id) {
    return window.posthog.get_distinct_id();
  }

  return undefined;
}

// ============================================================
// ZARAZ CONTEXT SEEDING — Zaraz Integration Worker (Phase 6)
// ============================================================

/**
 * Get or create a stable session ID stored in sessionStorage.
 *
 * The Zaraz Context Enricher uses this to deduplicate visitor records —
 * all events within one browser session return the same visitor_id.
 * Call this once on app init and pass the result to setZarazContext().
 */
export function getOrCreateSessionId(): string {
  if (typeof window === 'undefined') return crypto.randomUUID();
  const key = '__sbi_sid';
  const existing = sessionStorage.getItem(key);
  if (existing) return existing;
  const id = crypto.randomUUID();
  sessionStorage.setItem(key, id);
  return id;
}

/**
 * Seed the Zaraz client object with session / tenant context.
 *
 * Call once on app init, before the first event fires. The Zaraz
 * Context Enricher reads these client keys at `POST /zaraz/enrich`
 * to issue or retrieve a stable visitor_id for the session.
 *
 * @example
 * // In your root component or main.tsx useEffect:
 * setZarazContext({ sessionId: getOrCreateSessionId() });
 */
export function setZarazContext(opts: { sessionId?: string; tenantId?: string }): void {
  if (typeof window === 'undefined' || !window.zaraz?.set) return;
  if (opts.sessionId) window.zaraz.set('session_id', opts.sessionId);
  if (opts.tenantId) window.zaraz.set('tenant_id', opts.tenantId);
}

// ============================================================
// CONTACT SUBMIT MARK (#9146)
// ============================================================

const CONTACT_SUBMIT_KEY = '__sbi_contact_submitted';
/** A mark older than this is stale: a later load of the thank-you page is not that submit. */
export const CONTACT_SUBMIT_MARK_MS = 10 * 60 * 1000;

/**
 * #9146: the contact form marks a successful submit before it redirects to /thank-you/contact.
 * The thank-you page fires `contact_form_submitted` only when it finds the mark, so a direct
 * load, a reload or a shared link of that page is not counted as a submit.
 */
export function markContactSubmitted(now: number = Date.now()): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(CONTACT_SUBMIT_KEY, String(now));
  } catch {
    // Storage blocked: the thank-you page then counts nothing, never a false submit.
  }
}

/** Reads and clears the mark. True only for a fresh mark from this tab. */
export function consumeContactSubmitted(now: number = Date.now()): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const at = Number(sessionStorage.getItem(CONTACT_SUBMIT_KEY));
    sessionStorage.removeItem(CONTACT_SUBMIT_KEY);
    return Number.isFinite(at) && at > 0 && now - at >= 0 && now - at < CONTACT_SUBMIT_MARK_MS;
  } catch {
    return false;
  }
}

// Pre-defined event helpers for common actions

export const AnalyticsEvents = {
  // ========================================
  // CONVERSION EVENTS (High Priority)
  // ========================================

  // Form submissions
  formSubmitted: (formName: string, formLocation?: string) =>
    trackEvent('conversion.form_submitted', {
      form_name: formName,
      form_location: formLocation,
      path: window.location.pathname,
    }),

  // Calendar/Booking events
  calendarOpened: (calendarType: string = 'tidycal', page?: string) =>
    trackEvent('conversion.calendar_opened', {
      calendar_type: calendarType,
      page: page || window.location.pathname,
      path: window.location.pathname,
    }),

  bookingStarted: (calendarType: string, step: string) =>
    trackEvent('conversion.booking_started', {
      calendar_type: calendarType,
      step,
      path: window.location.pathname,
    }),

  // CTA clicks
  ctaClicked: (ctaText: string, ctaLocation: string, destination: string, label?: string) =>
    trackEvent('conversion.cta_clicked', {
      cta_label: label ?? ctaLabel(ctaLocation, ctaText),
      cta_text: ctaText,
      cta_location: ctaLocation,
      destination,
      path: window.location.pathname,
    }),

  // Legacy conversion CTA (for backward compatibility)
  conversionCTAClicked: (page: string, ctaType: string, destination: string, label?: string) =>
    trackEvent('conversion.cta_clicked', {
      cta_label: label ?? ctaLabel(page, ctaType),
      page,
      cta_type: ctaType,
      destination,
      path: window.location.pathname,
    }),

  // Downloads
  downloadStarted: (resourceTitle: string, resourceType: string, fileFormat: string) =>
    trackEvent('conversion.download_started', {
      resource_title: resourceTitle,
      resource_type: resourceType,
      file_format: fileFormat,
      path: window.location.pathname,
    }),

  // Newsletter signups
  newsletterSignup: (sourcePage?: string, placement?: string) =>
    trackEvent('conversion.newsletter_signup', {
      source_page: sourcePage || window.location.pathname,
      placement,
      path: window.location.pathname,
    }),

  // ========================================
  // FUNNEL CONVERSION EVENTS (WS6 — Priority tracking)
  // ========================================

  // Any "Explore Programs" or "SmallBiz Programs" CTA click
  programsCTAClicked: (location: string, destination: string = '/programs', label?: string) =>
    trackEvent('programs_cta_clicked', {
      cta_label: label ?? programsCtaLabel(location),
      location,
      destination,
      path: window.location.pathname,
    }),

  // Any "Book Free Consultation" CTA click
  freeConsultCTAClicked: (location: string, destination: string = '/free-consults', label?: string) =>
    trackEvent('free_consult_cta_clicked', {
      cta_label: label ?? freeConsultCtaLabel(location),
      location,
      destination,
      path: window.location.pathname,
    }),

  // HubServiceCTA → /programs click (hub name as property)
  hubToProgramsClicked: (hubName: string, hubCategory: string) =>
    trackEvent('hub_to_programs_clicked', {
      hub_name: hubName,
      hub_category: hubCategory,
      path: window.location.pathname,
    }),

  // Footer Programs card click
  footerProgramsClicked: () =>
    trackEvent('footer_programs_clicked', {
      path: window.location.pathname,
    }),

  // /thank-you-sign-up or /sign-up-thanks page load → newsletter signup completed
  newsletterSignupCompleted: (sourcePage?: string) =>
    trackEvent('newsletter_signup_completed', {
      source_page: sourcePage || window.location.pathname,
      path: window.location.pathname,
    }),

  // /thank-you/free-consult page load → consult booked
  freeConsultBooked: () =>
    trackEvent('free_consult_booked', {
      path: window.location.pathname,
    }),

  // #3652 — Ben's personal booking page (/scheduling/ben). These events are kept
  // OUT of the Free Consult funnel (free_consult_cta_clicked →
  // conversion.booking_started → free_consult_booked, website-analytics-api.ts), so
  // networking and partner calls on Ben's calendar never count as consult bookings.

  // /scheduling/ben TidyCal calendar loaded (per booking option)
  benBookingStarted: (bookingOption: string) =>
    trackEvent('ben_booking_started', {
      calendar_type: 'tidycal',
      booking_option: bookingOption,
      path: window.location.pathname,
    }),

  // /scheduling/ben/thanks page load (TidyCal after-booking redirect) → Ben call booked
  benBookingCompleted: () =>
    trackEvent('ben_booking_completed', {
      calendar_type: 'tidycal',
      path: window.location.pathname,
    }),

  // /thank-you/contact page load after a submit in this tab → contact form submitted
  // (#9146: the page checks consumeContactSubmitted() first).
  contactFormSubmitted: () =>
    trackEvent('contact_form_submitted', {
      path: window.location.pathname,
    }),

  // /thank-you/programs page load → program signup completed
  programSignupCompleted: (programTier?: string) =>
    trackEvent('program_signup_completed', {
      program_tier: programTier,
      path: window.location.pathname,
    }),

  // ========================================
  // ENGAGEMENT EVENTS
  // ========================================

  // Content viewed
  contentViewed: (contentType: string, title: string, slug: string, category?: string) =>
    trackEvent('engagement.content_viewed', {
      content_type: contentType,
      title,
      slug,
      category,
      path: window.location.pathname,
    }),

  // Read depth tracking
  contentReadDepth: (depthPercent: number, contentType: string, title: string) =>
    trackEvent('engagement.content_read_depth', {
      depth_percent: depthPercent,
      content_type: contentType,
      title,
      path: window.location.pathname,
    }),

  // Video interactions
  videoPlayed: (videoId: string, videoTitle: string, platform: string = 'cloudflare-stream') =>
    trackEvent('engagement.video_played', {
      video_id: videoId,
      video_title: videoTitle,
      platform,
      path: window.location.pathname,
    }),

  // Hub visits
  hubVisited: (hubName: string) =>
    trackEvent('engagement.hub_visited', {
      hub_name: hubName,
      path: window.location.pathname,
    }),

  // Search
  searchPerformed: (query: string, resultsCount: number, filterApplied?: string) =>
    trackEvent('engagement.search_performed', {
      query,
      results_count: resultsCount,
      filter_applied: filterApplied,
      path: window.location.pathname,
    }),

  // Resource filtering
  resourceFiltered: (filterType: string, filterValue: string, collection: string) =>
    trackEvent('engagement.resource_filtered', {
      filter_type: filterType,
      filter_value: filterValue,
      collection,
      path: window.location.pathname,
    }),

  // Legacy resource events (for backward compatibility)
  resourceDownloaded: (title: string, type: string, format: string) =>
    trackEvent('engagement.resource_downloaded', { title, type, format }),

  resourceViewed: (title: string, type: string) =>
    trackEvent('engagement.resource_viewed', { title, type }),

  // ========================================
  // NAVIGATION EVENTS
  // ========================================

  // Nav clicks
  navClicked: (navItem: string, destination: string) =>
    trackEvent('navigation.nav_item_clicked', {
      nav_item: navItem,
      destination,
      path: window.location.pathname,
    }),

  // External links
  externalLinkClicked: (url: string, text: string) =>
    trackEvent('navigation.external_link_clicked', {
      url,
      text,
      source_path: window.location.pathname,
    }),

  // Footer links
  footerLinkClicked: (linkText: string, destination: string, section: string) =>
    trackEvent('navigation.footer_link_clicked', {
      link_text: linkText,
      destination,
      section,
      path: window.location.pathname,
    }),

  // ========================================
  // PERFORMANCE EVENTS
  // ========================================

  // Page load performance
  pageLoadComplete: (durationMs: number, deviceType: string) =>
    trackEvent('performance.page_load', {
      duration_ms: durationMs,
      path: window.location.pathname,
      device_type: deviceType,
    }),

  // ========================================
  // ERROR EVENTS
  // ========================================

  // Client errors
  clientException: (errorMessage: string, errorStack: string, component?: string) =>
    trackEvent('error.client_exception', {
      error_message: errorMessage,
      error_stack: errorStack,
      component,
      path: window.location.pathname,
    }),

  // Content not found
  contentNotFound: (contentType: string, slug: string) =>
    trackEvent('error.content_not_found', {
      content_type: contentType,
      slug,
      path: window.location.pathname,
    }),

  // Legacy error event (for backward compatibility)
  errorOccurred: (errorType: string, message: string) =>
    trackEvent('error.client_exception', {
      error_type: errorType,
      message,
      path: window.location.pathname,
    }),

  // ========================================
  // AI SEARCH EVENTS
  // ========================================

  // Search modal/panel opened
  aiSearchOpened: (source: 'header' | 'search_page' | '404_page') =>
    trackEvent('ai_search_opened', {
      source,
      path: window.location.pathname,
    }),

  // Search query submitted
  aiSearchQuerySubmitted: (query: string, source: string, queryLength: number) =>
    trackEvent('ai_search_query_submitted', {
      query,
      source,
      query_length: queryLength,
      path: window.location.pathname,
    }),

  // Search completed successfully
  aiSearchCompleted: (
    query: string,
    resultCount: number,
    latencyMs?: number,
    hasSummary?: boolean
  ) =>
    trackEvent('ai_search_completed', {
      query,
      result_count: resultCount,
      latency_ms: latencyMs,
      has_summary: hasSummary,
      path: window.location.pathname,
    }),

  // Search result clicked
  aiSearchResultClicked: (
    query: string,
    resultUrl: string,
    resultTitle: string,
    position: number,
    score?: number
  ) =>
    trackEvent('ai_search_result_clicked', {
      query,
      result_url: resultUrl,
      result_title: resultTitle,
      result_position: position,
      result_score: score,
      path: window.location.pathname,
    }),

  // Zero results returned
  aiSearchZeroResults: (query: string) =>
    trackEvent('ai_search_zero_results', {
      query,
      path: window.location.pathname,
    }),

  // Search error occurred
  aiSearchError: (query: string, errorType: string, errorStatus?: number) =>
    trackEvent('ai_search_error', {
      query,
      error: errorType,
      status: errorStatus,
      path: window.location.pathname,
    }),

  // ========================================
  // LEGACY EVENTS (Deprecated, for backward compatibility)
  // ========================================

  // Legacy calendar interaction
  calendarInteraction: (action: string, calendarType: string = 'tidycal') =>
    trackEvent('calendar_interaction', {
      action,
      calendar_type: calendarType,
      path: window.location.pathname,
    }),

  // Legacy form field focused
  formFieldFocused: (formName: string, fieldName: string) =>
    trackEvent('form_field_focused', { form_name: formName, field_name: fieldName }),
};
